import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import {
  GeofenceSpeedLimitModel,
  useDeleteGeofenceSpeedLimitMutation,
  useListGeofenceSpeedLimitsQuery,
} from '@casops/store/apis/geofenceSpeedLimitsApi';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';

type Model = GeofenceSpeedLimitModel;

const modelName = ModelName.GeofenceSpeedLimit;
const sidePanel = SidePanel.ManageGeofenceSpeedLimit;
const permission = Permission.GeofenceManagement;

const canDeleteItem = (data: Model): boolean => !data.used;

const getItemName = (data: Model): string => `${data.speed}`;

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteGeofenceSpeedLimitMutation,
    getItemName,
  },
};

export function GeofenceSpeedLimits() {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'speed', headerName: t('Fields.Speed'), minWidth: 80, maxWidth: 200 },
      { field: 'desc', headerName: t('Fields.Description'), minWidth: 300 },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListGeofenceSpeedLimitsQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <CollectionView<Model>
      sliceKey={modelName}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={data}
      hasData={hasData}
      noDataDescription={noDataDescription}
      actions={
        <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
      }
    />
  );
}

export default GeofenceSpeedLimits;
